/**
 * Patient Next Steps Component
 * Shows remaining onboarding tasks and progress
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  LinearProgress,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Person,
  ContactPhone,
  History,
  CloudUpload,
  Assignment,
  ArrowForward,
  Dashboard,
  FitnessCenter
} from '@mui/icons-material';

const PatientNextSteps = ({ onboardingState, onSectionComplete, onContinueToDashboard }) => {
  if (!onboardingState) {
    return (
      <Box p={3}>
        <Alert severity="info">Loading onboarding information...</Alert>
      </Box>
    );
  }

  const { stage, sections_done, next_actions, trial } = onboardingState;

  // Calculate completion percentage
  const totalSections = 6; // Updated to include biometrics
  const completedSections = Object.values(sections_done || {}).filter(Boolean).length;
  const completionPercentage = Math.round((completedSections / totalSections) * 100);

  // Section configuration
  const sectionConfig = {
    profile: {
      icon: Person,
      title: 'Basic Profile',
      description: 'Your personal information and contact details'
    },
    biometrics: {
      icon: FitnessCenter,
      title: 'Biometrics & Health',
      description: 'Height, weight, vitals, and lifestyle information'
    },
    emergency: {
      icon: ContactPhone,
      title: 'Emergency Contact',
      description: 'Add someone we can contact in case of emergency'
    },
    history: {
      icon: History,
      title: 'Medical History',
      description: 'Share your medical background for better care'
    },
    uploads: {
      icon: CloudUpload,
      title: 'Upload Documents',
      description: 'Upload existing medical reports and documents'
    },
    intake: {
      icon: Assignment,
      title: 'Intake Form',
      description: 'Complete detailed health assessment'
    }
  };

  const getSectionStatus = (sectionKey) => {
    return sections_done?.[sectionKey] ? 'completed' : 'pending';
  };

  const handleSectionClick = (action) => {
    // For now, just mark as complete and show coming soon
    if (action.key === 'profile') {
      // Profile is already handled by the profile form
      return;
    }
    
    // For biometrics and documents, navigate to the actual forms
    if (action.key === 'biometrics' || action.key === 'uploads') {
      // Navigate to the respective form
      window.location.href = `/onboarding/patient/${action.key}`;
      return;
    }
    
    // For other sections, show coming soon and mark as complete
    onSectionComplete(action.key);
  };

  return (
    <Box p={3} maxWidth={800} mx="auto">
      <Card>
        <CardContent>
          {/* Header */}
          <Box mb={3}>
            <Typography variant="h4" gutterBottom>
              Complete Your Profile
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              You're off to a great start! Complete these steps to get the most out of MindWell.
            </Typography>
            
            {/* Progress */}
            <Box mt={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Overall Progress
                </Typography>
                <Chip 
                  label={`${completedSections}/${totalSections} Complete`}
                  color={completionPercentage >= 80 ? 'success' : 'primary'}
                  variant="outlined"
                />
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={completionPercentage} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Box>

          {/* Trial Info */}
          {trial?.is_active && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                🎉 You have {trial.days_remaining} days of free access to all MindWell features!
              </Typography>
            </Alert>
          )}

          {/* Section List */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Profile Sections
          </Typography>
          
          <List>
            {Object.keys(sectionConfig).map((sectionKey) => {
              const config = sectionConfig[sectionKey];
              const status = getSectionStatus(sectionKey);
              const IconComponent = config.icon;
              const isCompleted = status === 'completed';
              
              // Find corresponding next action
              const nextAction = next_actions?.find(action => action.key === sectionKey);
              
              return (
                <ListItem
                  key={sectionKey}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 2,
                    bgcolor: isCompleted ? 'success.50' : 'background.paper'
                  }}
                >
                  <ListItemIcon>
                    {isCompleted ? (
                      <CheckCircle color="success" />
                    ) : (
                      <RadioButtonUnchecked color="action" />
                    )}
                  </ListItemIcon>
                  
                  <ListItemIcon>
                    <IconComponent color={isCompleted ? 'success' : 'action'} />
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">
                          {config.title}
                        </Typography>
                        {isCompleted && (
                          <Chip 
                            label="Complete" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={config.description}
                  />
                  
                  {!isCompleted && nextAction && (
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ArrowForward />}
                        onClick={() => handleSectionClick(nextAction)}
                        disabled={sectionKey === 'intake'} // Intake is disabled for now
                      >
                        {sectionKey === 'intake' ? 'Coming Soon' : 'Complete'}
                      </Button>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              );
            })}
          </List>

          {/* Quick Actions */}
          <Box mt={4}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      🩺 Book an Appointment
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Ready to connect with a healthcare provider
                    </Typography>
                    <Button variant="outlined" size="small" disabled>
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      📱 Mobile App
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Get the MindWell mobile app for easy access
                    </Typography>
                    <Button variant="outlined" size="small" disabled>
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Actions */}
          <Box mt={4} display="flex" gap={2} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              You can complete these steps anytime from your dashboard
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              endIcon={<Dashboard />}
              onClick={onContinueToDashboard}
            >
              Go to Dashboard
            </Button>
          </Box>

          {/* Help Section */}
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Need help?</strong> Our support team is here to assist you. 
              Contact us anytime from your dashboard.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PatientNextSteps;
